import type { Appearance, Palette, PaletteSet } from "@picnic-field/renderer/appearance";
import { normalizePalette } from "@picnic-field/renderer/appearance";
import { ApiError, isFeCode } from "../http/errors";
import type { FeCode } from "../http/errors";
import type { Requester } from "../../types/api";

/**
 * FS-02 작품 올리기 계약(제어 평면 C-07 · Bearer). SR-INT-06 · SR-INT-02 · SR-CMP-01 · SR-MDF-04 · SR-MDF-06.
 * 셸은 계약을 그대로 받되, 확정 전 필드(보드 표기 · 목록 봉투)는 여기서만 너그럽게 읽는다.
 */
export type SourceStatus = "ready" | "expired" | "superseded";

/**
 * 차시(선택 — CRQ-002 D-8 제안 모양). 제어 평면은 지금 {id}(MDF meta.lessonId)만 보낸다.
 * no · title 이 오면 화면이 '5차시 · 오르막 자동차' 칩과 자동 작품명(차시+별명)을 그린다.
 * title 은 Campus 가 주는 표시 문구(로캘별)이지 i18n 키가 아니다.
 */
export interface LessonRef {
  id: string;
  no?: number | null;
  title?: { ko?: string | null; en?: string | null } | null;
}

export interface CampusSource {
  importId: string;
  modelSlug: string;
  glbVersion: string;
  title: string;
  appearance: Appearance | null;
  receivedAt: string;
  expiresAt: string;
  status: SourceStatus;
  /** 가져올 때 이미 알려진 문제(FE-10x) — 학생에게는 피코 문구로만 */
  feCode?: string | null;
  /** V-07 구동 바퀴 판정 경고 — 교사 전용(학생 화면에 안 보임) */
  wheelWarning?: boolean;
  refetchedAt?: string | null;
  /** 선택 — 차시(와이어 FS-02-01 '5차시') */
  lesson?: LessonRef | null;
  /** 선택 — Campus 조립 썸네일(색 저장본). 없으면 칠한 색 견본으로 대신한다 */
  thumbnailUrl?: string | null;
  /** 선택 — 자산 판("prime" = 스파이크 프라임 교구 · "" 또는 없음 = 스마트 브릭) · 옛 응답엔 없음 */
  variant?: string | null;
}

export interface BenchSource {
  projectId: string;
  title: string;
  versionHash: string | null; // null = 저장본 없음(초안) → 확정 시 FE-201
  board: string;
  updatedAt: string;
  /** 작품의 차시(Bench lesson_meta · 예: MB-L06) — 놀이터가 차시에 맞는 장면을 고른다(PICNIC-MB-FIELD-001 §6.3) · 모르면 null · 옛 응답엔 없음 */
  lessonId?: string | null;
}

export interface Sources {
  campus: CampusSource[];
  bench: BenchSource[];
  /** false = Bench 에 닿지 못함(bench 는 빈 배열) — 전시만 가능 */
  benchAvailable: boolean;
}

export type IssueSeverity = "error" | "warning";

export interface ValidationIssue {
  rule: string;
  code: string;
  severity: IssueSeverity;
  path?: string;
  detail?: string;
}

export interface RigSummary {
  dynamicBodies: number;
  kinematicColliders: number;
  wheels: number;
  subBodies: number;
}

export interface PreviewRef {
  modelSlug: string;
  glbVersion: string;
  glbUrl: string;
  appearance: Appearance | null;
}

export interface ValidateResult {
  ok: boolean;
  issues: ValidationIssue[];
  tags: string[];
  rig?: RigSummary;
  /** 422(실패) 응답에는 없을 수 있다 */
  preview?: PreviewRef;
  board?: string | null;
  /** 정적 전시(전시만 · micro:bit · V-07) — 확정 전 표시용 */
  static?: boolean;
}

export interface Snapshot {
  snapshotId: string;
  title: string;
  modelSlug: string;
  glbVersion: string;
  appearance: Appearance | null;
  tags: string[];
  board: string | null;
  static: boolean;
  createdAt: string;
  /** 계약에 없음(S2) — 있으면 쓰고, 없으면 올리기 때 받은 미리보기 URL 을 쓴다 */
  glbUrl?: string;
  /** 선택 — 차시(상단 바 차시 칩 · 자동 작품명) */
  lesson?: LessonRef | null;
  /** P2-S6 올리기 응답에만 — 룸이 열려 있으면 핫스왑 결과(SR-RUN-05 · 계약 §3.5). 룸이 없으면 없음 */
  hotswap?: HotSwap | null;
  /**
   * Sprint 4 — 이 스냅샷이 고정한 Bench 프로젝트 id('Bench 에서 고치기' 입장권 · SR-INT-02). 본인 스냅샷에만 오고,
   * 프로그램 없이 올렸거나 남의 스냅샷 · 옛 제어 평면이면 null/없음(→ Bench 첫 화면으로 연다).
   */
  programProjectId?: string | null;
}

/** 핫스왑 종류 — program 프로그램만 · model 조립이 바뀌어 그 기계만 리스폰 · appearance 색만 */
export type SwapChange = "program" | "model" | "appearance";
export interface HotSwap {
  applied: boolean;
  change: SwapChange | null;
  /** P3-S2 릴레이 — 내 구간이 도는 중이라 다음 시도(Armed)에 적용된다(hotswap.deferred) */
  deferred?: boolean;
}

const SWAP_CHANGES: readonly string[] = ["program", "model", "appearance"];
export const isSwapChange = (v: unknown): v is SwapChange =>
  typeof v === "string" && SWAP_CHANGES.includes(v);

/** 응답의 hotswap 필드를 너그럽게 읽는다(없거나 모양이 어긋나면 null) */
export function readHotSwap(v: unknown): HotSwap | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  if (typeof o.applied !== "boolean" && o.deferred !== true) return null;
  return {
    applied: o.applied === true,
    change: isSwapChange(o.change) ? o.change : null,
    ...(o.deferred === true ? { deferred: true } : {}),
  };
}

/** P3-S2 릴레이 — 끝난 구간(완료·건너뜀·완성)이라 룸이 코드 교체를 받지 않았다(409 { field:"locked" } · DEC-003 §9) */
export function isSwapLocked(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.status !== 409) return false;
  const b = e.body as { field?: unknown; error?: unknown } | null;
  return b?.field === "locked" || b?.error === "locked";
}

/** 되돌리기 결과 — done 되돌림 · already 이미 한 번 되돌렸다(409 — 1회만) */
export type RevertResult = "done" | "already";

export interface UploadSelection {
  importId: string;
  /** 없으면 '코딩 없이 전시만 하기'(조립만 — 정적 전시) */
  benchProjectId?: string;
  /**
   * Bench 저장본 고정(CRQ-004 D-4) — 'Bench 에서 고치기' 에서 돌아온 `sha256:…`. 주면 그 저장본만(없으면 FE-201 · 다르면 FE-204).
   * benchProjectId 가 있을 때만 보낸다.
   */
  benchVersionHash?: string;
}

/** 422 스냅샷 거절 */
export class SnapshotRejected extends Error {
  constructor(
    readonly code: FeCode,
    readonly issues: ValidationIssue[],
  ) {
    super(code);
    this.name = "SnapshotRejected";
  }
}

export type BoardKind = "spike" | "microbit" | "other";

/** 보드 표기 정규화 — "SPIKE Prime" · "spike" · "micro:bit" · "microbit" … — 그 밖(모르는 보드)은 other */
export function boardKind(board: string | null | undefined): BoardKind {
  const b = (board ?? "").toLowerCase().replace(/[^a-z]/g, "");
  if (b.startsWith("spike")) return "spike";
  if (b.startsWith("microbit")) return "microbit";
  return "other";
}

const listOf = <T>(v: unknown, ...keys: string[]): T[] => {
  if (Array.isArray(v)) return v as T[];
  const o = (v ?? {}) as Record<string, unknown>;
  for (const k of keys) if (Array.isArray(o[k])) return o[k] as T[];
  return [];
};

export interface FieldApi {
  sources(): Promise<Sources>;
  refreshImport(importId: string): Promise<CampusSource>;
  validate(sel: UploadSelection): Promise<ValidateResult>;
  createSnapshot(sel: UploadSelection): Promise<Snapshot>;
  /**
   * 'Bench 에서 고치기' 입장권(2026-09-28) — POST /api/courses/{c}/bench/handoff {projectId, returnUrl} → {editorUrl}(티켓은 프래그먼트).
   * 오류는 FE-201(404 남의 프로젝트 · 502 returnUrl 거절 · 503 Bench 불통) · 429 · 422 그대로 ApiError.
   */
  benchHandoff(projectId: string, returnUrl: string): Promise<{ editorUrl: string }>;
  /** P2-S6 룸 핫스왑 되돌리기(직전 스냅샷 · 1회) — POST /api/courses/{c}/me/room/revert */
  revertRoom(): Promise<RevertResult>;
  mySnapshots(): Promise<Snapshot[]>;
  snapshot(id: string): Promise<Snapshot>;
  /**
   * 팔레트를 못 받으면 null(모형 원래 색 — 오류 아님, SR-MDF-06). name = 작품의 appearance.palette(2026-09-30 팔레트 버전 전환 —
   * Campus 현행이 smartblock-v1 이어도 brick-v1 작품은 brick-v1 색). 이름이 없으면 현행(GET /api/palette).
   */
  palette(name?: string | null): Promise<Palette | null>;
  /** 여러 작품을 한 장면에 — 이름마다 팔레트(못 받은 이름은 빠진다 → 그 작품은 원래 색) */
  palettes(names: Iterable<string | null | undefined>): Promise<PaletteSet>;
  /**
   * FS-01-03 내 아바타(카탈로그 키 · S4 계약). 못 받으면 null(기본 아바타), 저장 실패는 false.
   * GET/PUT /api/courses/{c}/me/avatar {avatarKey}
   */
  myAvatar(): Promise<string | null>;
  saveAvatar(avatarKey: string): Promise<boolean>;
  /**
   * FS-01-04 · FS-11-PH1 교사 개요. 제어 평면에 아직 없음(S5) — 404 면 null(화면은 '활동 없음' 상태).
   * 제안 계약: GET /api/courses/{c}/teacher/overview
   */
  teacherOverview(): Promise<TeacherOverview | null>;
}

/** 교사 개요(선택 — 제안 계약). 순위·점수 필드 없음(DR-01): 제출은 n/전체와 미제출 이름만. */
export type Visibility = "class" | "selected" | "private";
/** 활동 상태 — P2-S5 계약 §2.2: scheduled 예정 · ready 룸 예열/열림 · done 룸 닫힘 */
export type ActivityStatus = "scheduled" | "ready" | "done";

export interface TeacherActivity {
  id: string;
  lessonNo?: number | null;
  title: string;
  mode?: string | null;
  preset?: string | null;
  minutes?: number | null;
  visibility?: Visibility | null;
  when?: string | null;
  status: ActivityStatus;
  uploaded?: number | null;
  total?: number | null;
  /** P2-S5(계약 §2.2) — 모드 id · 프리셋 키 · 시간 예산 · 예정 시각(ISO) · 마무리 템플릿 */
  modeId?: string | null;
  presetKey?: string | null;
  timeBudgetS?: number | null;
  scheduledAt?: string | null;
  scriptTemplate?: "view" | "challenge" | "team" | null;
  /** P3-S3(계약 §3) — 그 활동에서 나온 영상을 내려받을 수 있다(교사 홈 '영상 준비됨' 칩) */
  videoReady?: boolean;
  /** 그 활동의 (마지막) 룸 id — 영상 탭 경로. 없으면 코스 전체 영상(/teacher/room/recent/video) */
  roomId?: string | null;
}

/** 활동 표의 세 갈래(와이어 FS-01-04 — 예열 준비 · 작품 n/전체 · 종료) — 모르는 값은 예정으로 */
export function activityPhase(s: string): ActivityStatus {
  return s === "ready" || s === "done" ? s : "scheduled";
}

export interface TeacherWork {
  snapshotId: string;
  displayName: string;
  title: string;
  tags: string[];
  static: boolean;
  /** 보드 표기(SPIKE · micro:bit …) — 정적 전시 칩에 쓴다 */
  board?: string | null;
  /** 스티커 종류별 수(정렬 없음 — 이름순 고정) */
  stickers?: { emoji: string; count: number }[];
}

export interface TeacherOverview {
  courseLabel?: string | null;
  courseCount?: number | null;
  activities?: TeacherActivity[];
  submissions?: { lessonNo?: number | null; uploaded: number; total: number; missing?: string[] };
  visibility?: Visibility | null;
  preset?: string | null;
  minutes?: number | null;
  stickerTotal?: number | null;
  works?: TeacherWork[];
}

export function createFieldApi(request: Requester, courseId: string): FieldApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}`;
  /** 이름별 팔레트 약속(받은 것만 기억 — 실패 · 없음은 다음에 다시 묻는다) */
  const paletteMemo = new Map<string, Promise<Palette | null>>();
  const paletteOf = (name: string | null): Promise<Palette | null> => {
    const key = name ?? "";
    const hit = paletteMemo.get(key);
    if (hit) return hit;
    const p = (async () => {
      try {
        const raw = await request<{ source?: unknown } | null>(
          name ? `/api/palette/${encodeURIComponent(name)}` : "/api/palette",
        );
        // source: campus | cache | none — none 이면 팔레트 없음(모형 원래 색)
        if (raw?.source === "none") return null;
        const n = normalizePalette(raw);
        return n && n.colors.length ? n : null;
      } catch {
        return null;
      }
    })();
    paletteMemo.set(key, p);
    void p.then((v) => v || paletteMemo.delete(key));
    return p;
  };
  const post = (body: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body) });
  const selBody = (s: UploadSelection) =>
    s.benchProjectId
      ? {
          importId: s.importId,
          benchProjectId: s.benchProjectId,
          ...(s.benchVersionHash ? { benchVersionHash: s.benchVersionHash } : {}),
        }
      : { importId: s.importId };

  return {
    async sources() {
      const v = await request<Partial<Sources>>(`${c}/me/sources`);
      return {
        campus: listOf(v?.campus),
        bench: listOf(v?.bench),
        benchAvailable: v?.benchAvailable !== false,
      };
    },
    refreshImport: (id) =>
      request<CampusSource>(`${c}/imports/${encodeURIComponent(id)}/refresh`, { method: "POST" }),
    async validate(sel) {
      try {
        return await request<ValidateResult>(`${c}/uploads/validate`, post(selBody(sel)));
      } catch (e) {
        // 오류 이슈가 있으면 422 {code, ok:false, issues} — 실패 결과로 돌려준다
        if (e instanceof ApiError && e.status === 422) {
          const b = (e.body ?? {}) as Partial<ValidateResult> & { code?: unknown };
          const issues = listOf<ValidationIssue>(b.issues);
          const code = typeof b.code === "string" ? b.code : e.code;
          return {
            ...b,
            ok: false,
            issues: issues.length ? issues : [{ rule: "", code, severity: "error" }],
            tags: listOf<string>(b.tags),
          };
        }
        throw e;
      }
    },
    async createSnapshot(sel) {
      try {
        const s = await request<Snapshot>(`${c}/snapshots`, post(selBody(sel)));
        // P3-S2 — 릴레이 미룸은 hotswap { applied:false, change, deferred:true }
        return s && typeof s === "object" ? { ...s, hotswap: readHotSwap(s.hotswap) } : s;
      } catch (e) {
        if (e instanceof ApiError && e.status === 422) {
          const b = (e.body ?? {}) as { code?: unknown; issues?: unknown };
          const code: FeCode = isFeCode(b.code) ? b.code : "FE-101";
          throw new SnapshotRejected(code, listOf<ValidationIssue>(b.issues));
        }
        throw e;
      }
    },
    async benchHandoff(projectId, returnUrl) {
      const v = await request<{ editorUrl?: unknown } | null>(
        `${c}/bench/handoff`,
        post({ projectId, returnUrl }),
      );
      if (typeof v?.editorUrl !== "string")
        throw new ApiError("NET-SERVER", 200, "handoff without editorUrl", v);
      return { editorUrl: v.editorUrl };
    },
    async revertRoom() {
      try {
        await request<unknown>(`${c}/me/room/revert`, { method: "POST" });
        return "done";
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) return "already";
        throw e;
      }
    },
    async mySnapshots() {
      const v = await request<unknown>(`${c}/snapshots/mine`);
      return listOf<Snapshot>(v, "items", "snapshots");
    },
    snapshot: (id) => request<Snapshot>(`${c}/snapshots/${encodeURIComponent(id)}`),
    async myAvatar() {
      try {
        const v = await request<{ avatarKey?: unknown } | null>(`${c}/me/avatar`);
        return typeof v?.avatarKey === "string" ? v.avatarKey : null;
      } catch {
        return null;
      }
    },
    async saveAvatar(avatarKey) {
      try {
        await request(`${c}/me/avatar`, { method: "PUT", body: JSON.stringify({ avatarKey }) });
        return true;
      } catch {
        return false;
      }
    },
    async teacherOverview() {
      try {
        return await request<TeacherOverview>(`${c}/teacher/overview`);
      } catch (e) {
        if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return null;
        throw e;
      }
    },
    palette(name) {
      return paletteOf(name ?? null);
    },
    async palettes(names) {
      const uniq = [...new Set([...names].filter((n): n is string => !!n))].sort();
      const got = await Promise.all(uniq.map((n) => paletteOf(n)));
      const out = new Map<string, Palette>();
      // 이름이 맞는 것만(서버가 다른 이름을 돌려주면 그 작품은 원래 색 — 이름 대조 규칙)
      got.forEach((p, i) => p && p.palette === uniq[i] && out.set(uniq[i]!, p));
      return out;
    },
  };
}

/** 가장 최근 스냅샷(= 현재 작품, SR-MDF-04) */
export function latestSnapshot(list: readonly Snapshot[]): Snapshot | null {
  let best: Snapshot | null = null;
  for (const s of list) if (!best || Date.parse(s.createdAt) > Date.parse(best.createdAt)) best = s;
  return best;
}
